import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box, Heading, Text, Link, Spinner, VStack, HStack, Grid, Image,
  Flex, Container, GridItem, Card, CardBody, Badge, Divider,
  Icon, Accordion, AccordionItem, AccordionButton, AccordionPanel,
  AccordionIcon, SimpleGrid, Stat, StatLabel, StatNumber
} from "@chakra-ui/react";
import {
  IoCalendarOutline, IoCallOutline, IoGlobeOutline, IoLocationOutline,
  IoPersonOutline, IoSchoolOutline, IoTimeOutline, IoDocumentsOutline,
  IoLinkOutline
} from "react-icons/io5";

import ApiResponse from "../../services/ApiResponse";
import { Carrera } from "../../services/models/Carrera";
import MapConfig from "../../MapConfig";
import UnauthorizedPage from "../UnauthorizedPage";
import ServerErrorPage from "../ServerErrorPage";

// 1. Helper externo para formateo (Senior Clean Code)
const formatDate = (dateISO: string | undefined) => {
  if (!dateISO) return "No definida";
  const date = new Date(dateISO);
  return isNaN(date.getTime()) 
    ? "Fecha inválida" 
    : date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

function CarreraPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [response, setResponse] = useState(new ApiResponse<Carrera>());

  useEffect(() => {
    const fetchCarrera = async () => {
      const apiResponse = new ApiResponse<Carrera>();
      await apiResponse.useFetch(`carreras/${id}`, "GET");
      setResponse(apiResponse);
    };
    fetchCarrera();
  }, [id]);

  const { data, loading, responseCode } = response;

  // Estilo Glassmorphism compartido
  const glassStyle = {
    bg: "rgba(255, 255, 255, 0.05)",
    backdropFilter: "blur(10px)",
    border: "1px solid",
    borderColor: "whiteAlpha.200",
    borderRadius: "2xl",
    color: "white"
  };

  // 2. Manejo de Estados de Carga y Error
  if (loading) return (
    <Flex justify="center" align="center" height="100vh" bg="blue.900">
      <Spinner size="xl" thickness="4px" speed="0.65s" color="orange.400" />
    </Flex>
  );

  if (responseCode === 401) return <UnauthorizedPage />;
  if (responseCode !== 200 || !data) return <ServerErrorPage />;

  return (
    <Box bgGradient="linear(to-b, blue.900, black)" minH="100vh" py={10} color="white">
      <Container maxW="container.xl">
        <Grid templateColumns={{ base: "1fr", lg: "2.5fr 1fr" }} gap={8}>
          
          {/* COLUMNA IZQUIERDA: Info de Carrera */}
          <GridItem>
            <VStack align="stretch" spacing={6}>
              <Box {...glassStyle} p={{ base: 6, md: 10 }}>
                <VStack align="start" spacing={4}>
                  <HStack justify="space-between" w="full">
                    <Badge colorScheme="orange" variant="solid" px={4} py={1} borderRadius="full">
                      {data.modalidad}
                    </Badge>
                    <Text fontSize="sm" color="whiteAlpha.600">ID: #{data.id}</Text>
                  </HStack>
                  
                  <Heading size="2xl" lineHeight="shorter" letterSpacing="tight">
                    {data.nombre}
                  </Heading>
                  
                  <HStack spacing={4} color="orange.300" fontWeight="bold">
                    <Icon as={IoSchoolOutline} />
                    <Text fontSize="lg">{data.tipo}</Text>
                  </HStack>

                  <Text fontSize="lg" color="whiteAlpha.800" lineHeight="tall">
                    {data.descripcion}
                  </Text>

                  <Divider borderColor="whiteAlpha.200" my={4} />

                  {/* Grid de Stats Rápidas */}
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={10} w="full">
                    <QuickStat icon={IoTimeOutline} label="Duración" value={`${data.duracion_anios} años`} />
                    <QuickStat icon={IoCalendarOutline} label="Turno" value={data.turno} />
                    <QuickStat icon={IoPersonOutline} label="Cupo" value={`${data.cupo || 0} alumnos`} />
                  </SimpleGrid>
                </VStack>
              </Box>

              {/* Acordeón de Plan de Estudios con mejor UX */}
              <Accordion allowToggle>
                <AccordionItem mb={4}>
                  <AccordionButton py={5} _hover={{ bg: "whiteAlpha.100" }}>
                    <HStack flex="1" textAlign="left">
                      <Icon as={IoDocumentsOutline} boxSize={5} color="orange.300" />
                      <Text fontWeight="bold">Plan de Estudio Detallado</Text>
                    </HStack>
                    <AccordionIcon />
                  </AccordionButton>
                  <AccordionPanel pb={6} px={8} color="whiteAlpha.800">
                    {data.plan_de_estudio ? (
                      data.plan_de_estudio.split('\n').map((line, i) => (
                        <Text key={i} mb={2} borderLeft="2px solid" borderColor="orange.400" pl={4}>
                          {line.trim()}
                        </Text>
                      ))
                    ) : (
                      <Text fontStyle="italic">Información no disponible por el momento.</Text>
                    )}
                  </AccordionPanel>
                </AccordionItem>
              </Accordion>

              {/* Fechas Importantes */}
              <Box {...glassStyle} p={6}>
                <Heading size="sm" mb={6} textTransform="uppercase" letterSpacing="widest" color="orange.200">
                  Cronograma de Ingreso
                </Heading>
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                  <DateBox label="Inicio Inscripción" date={data.fecha_inscripcion} />
                  <DateBox label="Cierre Inscripción" date={data.fecha_finalizacion_inscripcion} color="red.300" />
                  <DateBox label="Inicio de Cursado" date={data.fecha_inicio_cursado} color="green.300" />
                </SimpleGrid>
              </Box>
            </VStack>
          </GridItem>

          {/* COLUMNA DERECHA: Institución */}
          <GridItem>
            <VStack spacing={6} position="sticky" top="100px">
              <Card {...glassStyle} w="full" overflow="hidden">
                <Box bg="white" p={8} textAlign="center">
                  <Image
                    src={`${MapConfig.API_IMAGES_URL}${data.institucion_logo}`}
                    alt={data.institucion_nombre}
                    maxH="150px"
                    mx="auto"
                    objectFit="contain"
                  />
                </Box>
                <CardBody p={6}>
                  <VStack align="stretch" spacing={5}>
                    <VStack align="start" spacing={1}>
                      <Text fontSize="xs" color="whiteAlpha.500" fontWeight="black" textTransform="uppercase">
                        Dictado por:
                      </Text>
                      <Heading size="md" color="orange.400">{data.institucion_nombre}</Heading>
                    </VStack>

                    <Divider borderColor="whiteAlpha.200" />

                    <VStack align="stretch" spacing={4}>
                      <IconInfo icon={IoLocationOutline} text={data.institucion_direccion} />
                      <IconInfo icon={IoCallOutline} text={data.institucion_tel} isTel />
                      <IconInfo icon={IoGlobeOutline} text={data.institucion_pagina} isLink />
                    </VStack>

                    <ButtonLink href={data.inscripcion} />
                  </VStack>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}


const QuickStat = ({ icon, label, value }: any) => (
  <HStack spacing={3}>
    <Flex bg="orange.400" p={2} borderRadius="lg" color="blue.900">
      <Icon as={icon} boxSize={5} />
    </Flex>
    <VStack align="start" spacing={0}>
      <Text fontSize="xs" color="whiteAlpha.600" textTransform="uppercase">{label}</Text>
      <Text fontWeight="bold" fontSize="md">{value}</Text>
    </VStack>
  </HStack>
);

const DateBox = ({ label, date, color = "white" }: any) => (
  <VStack align="start" p={4} bg="whiteAlpha.50" borderRadius="xl" border="1px solid" borderColor="whiteAlpha.100">
    <Text fontSize="xs" color="whiteAlpha.500" fontWeight="bold">{label}</Text>
    <Text fontWeight="bold" color={color}>{formatDate(date)}</Text>
  </VStack>
);

const IconInfo = ({ icon, text, isLink, isTel }: any) => (
  <HStack align="start" spacing={3}>
    <Icon as={icon} color="orange.300" boxSize={5} mt={1} />
    {isLink ? (
      <Link href={text} isExternal fontSize="sm" color="blue.300" noOfLines={1} _hover={{ color: "orange.300" }}>{text}</Link>
    ) : isTel ? (
      <Link href={`tel:${text}`} fontSize="sm" _hover={{ color: "orange.300" }}>{text}</Link>
    ) : (
      <Text fontSize="sm" color="whiteAlpha.800">{text}</Text>
    )}
  </HStack>
);

const ButtonLink = ({ href }: { href?: string }) => (
  <Link 
    href={href} 
    isExternal 
    w="full" 
    _hover={{ textDecoration: 'none' }}
  >
    <Flex 
      bg="orange.500" 
      color="white" 
      p={4} 
      borderRadius="xl" 
      justify="center" 
      align="center" 
      fontWeight="bold"
      transition="0.3s"
      _hover={{ bg: "orange.600", transform: "translateY(-2px)", boxShadow: "0 4px 20px rgba(233, 104, 31, 0.4)" }}
    >
      <Icon as={IoLinkOutline} mr={2} />
      INSCRIBIRME AHORA
    </Flex>
  </Link>
);

export default CarreraPage;